import { Code, ConnectError } from '@connectrpc/connect';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/authContext';
import { agentClient, collabClient } from '../lib/connect/clients';
function bareChatId(chatId) {
    return chatId.startsWith('shanks-') ? chatId.slice(7) : chatId;
}
// Map Connect Codes to HTTP-like statuses so the calling component's
// 403/404/410 routing keeps working after the cut from REST.
function connectCodeToHTTPStatus(code) {
    switch(code){
        case Code.NotFound:
            return 404;
        case Code.PermissionDenied:
        case Code.Unauthenticated:
            return 403;
        case Code.FailedPrecondition:
            return 410;
        default:
            return 500;
    }
}
/**
 * Hook to determine the current user's access level for a chat.
 *
 * Two paths:
 *  - With a token → resolve via CollabService.ResolveShareToken (viewer
 *    flow). The token is the chat capability; the JWT gives us the
 *    caller's tenant for the same-tenant check.
 *  - Without a token → call AgentService.GetConversation. Shanks allows the
 *    owner or an active direct recipient; ownership is the userId match.
 */ export function useChatAccess(options) {
    const { chatId, token, skip } = options;
    const { user } = useAuth();
    const userId = user?.id;
    const [accessContext, setAccessContext] = useState(null);
    const [loading, setLoading] = useState(!skip);
    const [error, setError] = useState(null);
    const [errorStatus, setErrorStatus] = useState(null);
    const isMounted = useRef(true);
    const fetchAccess = useCallback(async ()=>{
        if (skip || !chatId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        setErrorStatus(null);
        try {
            if (token) {
                // Viewer path. ResolveShareToken returns the chat title +
                // sharer name; the chat_id it returns may differ from the
                // requested one if the token rotated, but for access purposes
                // we just need to know the call succeeded.
                const resp = await collabClient.resolveShareToken({
                    token
                });
                if (!isMounted.current) return;
                setAccessContext({
                    isOwner: false,
                    isShared: true,
                    role: 'viewer',
                    sharedBy: {
                        name: resp.sharedByName,
                        email: ''
                    },
                    sharedAt: ''
                });
            } else {
                // Owner path. GetConversation rejects with CodeNotFound when
                // the caller doesn't own the row (shanks's existence-leak
                // guard — see requireConversationOwnership).
                const response = await agentClient.getConversation({
                    id: bareChatId(chatId)
                });
                if (!isMounted.current) return;
                setAccessContext({
                    isOwner: !response.readOnly,
                    isShared: response.readOnly,
                    role: response.readOnly ? 'viewer' : 'owner'
                });
            }
        } catch (err) {
            if (!isMounted.current) return;
            let message = err instanceof Error ? err.message : 'An unexpected error occurred';
            let status = null;
            if (err instanceof ConnectError) {
                status = connectCodeToHTTPStatus(err.code);
                switch(status){
                    case 403:
                        message = "You don't have access to this chat";
                        break;
                    case 404:
                        message = 'Chat not found';
                        break;
                    case 410:
                        message = 'This share link has expired';
                        break;
                    default:
                        message = 'Failed to load chat';
                }
            }
            setError(message);
            setErrorStatus(status);
            setAccessContext(null);
        } finally{
            if (isMounted.current) {
                setLoading(false);
            }
        }
    }, [
        chatId,
        token,
        skip,
        userId
    ]);
    useEffect(()=>{
        isMounted.current = true;
        fetchAccess();
        return ()=>{
            isMounted.current = false;
        };
    }, [
        fetchAccess
    ]);
    return {
        accessContext,
        loading,
        error,
        errorStatus,
        refetch: fetchAccess
    };
}
